<template>
<v-app
  id="app"
  :style="cssVars"
>

<v-overlay
  :model-value="inIntro"
  :style="cssVars"
  id="intro-background"
>

<v-dialog 
  v-model="inIntro"
        >
        <div v-if="inIntro" id="introduction-overlay" class="elevation-10 gradient-background">
          <v-window v-model="introSlide">
            <template v-slot:additional>
              <div id="intro-window-close-button">
              <font-awesome-icon
                size="xl"
                class="ma-3"
                color="#b3d5e6"
                icon='square-xmark'
                @click="inIntro = !inIntro"
                @keyup.enter="inIntro = !inIntro"
                tabindex="0"
                tooltip-location="start"
              /> 
            </div>
            </template>

            <v-window-item :value="1"
              id="splash-screen"
            >
              <div
                id="first-splash-row"
              >
                <div id="splash-screen-text">
                  What Is in the Air You Breathe?
                </div>
                <div>
                  Explore daily pollution maps over North America and find out.
                </div>
              </div>

              <div>
                <v-btn
                  class="splash-get-started"
                  @click="introSlide++"
                  @keyup.enter="introSlide++"
                  :color="accentColor"
                  :density="display.smAndDown ? 'compact' : 'default'"
                  size="x-large"
                  variant="elevated"
                  rounded="lg"
                >
                  Get Started
                </v-btn>
              </div>
            
              <div id="splash-screen-acknowledgements">
                Brought to you by <a href="https://www.cosmicds.cfa.harvard.edu/" target="_blank" rel="noopener noreferrer">Cosmic Data Stories</a>.
                
                <div id="splash-screen-logos">
                  <a href="https://www.si.edu/" target="_blank" rel="noopener noreferrer"
                  ><img alt="Smithsonian Logo" src="220px-Smithsonian_sun_logo_no_text.svg.png"></a>
                  <credit-logos
                    :default-logos="['cosmicds', 'sciact', 'nasa']"
                  />
                </div>
              </div>
            </v-window-item>
            
            <v-window-item :value="2">
              <div class="intro-text">
                <p class="mb-5">
                  The TEMPO satellite mission (Tropospheric Emissions: Monitoring Pollution), launched in April 2023, is the first space-based instrument to measure major air pollutants across the North American continent every daylight hour at high spatial resolution. A collaboration between NASA and the Smithsonian Astrophysical Observatory, the TEMPO instrument gathers hourly daytime scans of the atmosphere over North America from the Atlantic Ocean to the Pacific Coast and from the Yucatán Peninsula to central Canada.
                </p>
              </div>
            </v-window-item>
            
            <v-window-item :value="3">
              <div class="intro-text mb-3">
                <p class="mb-3">
                  This Data Story provides an introduction to what can be learned from TEMPO’s data, which became publicly available May 20, 2024. The map here visualizes hourly Nitrogen Dioxide (NO<sub>2</sub>) data over time. NO<sub>2</sub> can be produced by:
                </p> 
                <ul>
                  <li>Burning of fossil fuels&#8212;for example from vehicles, power plants, manufacturing sites, and oil refineries</li>
                  <li>Fires and biomass burning&#8212;including wildfires and prescribed burns, as well as burning of vegetation for land clearing</li>
                  <li>Bacteria, which naturally convert nitrogen in soil into compounds that can form NO<sub>2</sub>. Agricultural use of nitrogen-based fertilizers increases the amount of NO<sub>2</sub> produced by these bacteria.</li>
                  <li>Lightning, which triggers a chemical reaction that turns harmless N<sub>2</sub> in the atmosphere into NO<sub>2</sub>.
</li>
                </ul>
                <p class="mt-3">
                For each date, you can see the scans beginning on the East Coast in the morning, and ending on the West Coast as the Sun sets.
                </p> 
              </div>
            </v-window-item>
            <v-window-item :value="4">
              <div class="intro-text mb-3">      
                <p class="mb-3">
                  In this interactive page you can:
                </p>
                <ul>
                  <li>
                    Use the search box to navigate a location of your choice.
                  </li>
                  <li>
                    Select a date and press the “Play” button or scroll the time slider to view the changing concentrations of NO<sub>2</sub> on those dates. 
                  </li>
                  <li>
                    Click <v-icon style="color: #ffcc33">mdi-share-variant</v-icon> to share your selected location, date, and time with others.
                  </li>
                  <li v-bind:style="cssVars">
                    Press the <v-icon style="font-size: 1.3em; color: var(--accent-color)" elevation="1">mdi-information-variant-circle-outline</v-icon> button next to each Featured Topic to get an overview of what you can explore in the TEMPO data.
                  </li>
                  <li>
                    For each Featured Topic, select Example Cases to investigate specific pollution events.
                  </li>
                  <li>
                    You can use the “Timezone” setting to investigate how pollution evolves over the day in different parts of the country, for example as rush hour progresses in large cities.
                  </li>
                </ul>
                <!-- add do not show introduction again button -->
                <v-checkbox
                  v-model="dontShowIntro"
                  @keyup.enter="dontShowIntro = !dontShowIntro"
                  label="Don't show this introduction at launch"
                  color="#c10124"
                  hide-details
                />
              </div>
            </v-window-item>
          </v-window>

          <div id="intro-bottom-controls">
            <div>
              <v-btn
                v-if="(introSlide > 1)"
                id="intro-next-button"
                :color="accentColor"
                @click="introSlide--"
                @keyup.enter="introSlide--"
                elevation="0"
                >
                Back
              </v-btn>
            </div>
            
            <v-btn
              v-if="(introSlide > 1)"
              id="intro-next-button"
              :color="accentColor"
              @click="introSlide++"
              @keyup.enter="introSlide++"
              elevation="0"
              >
              {{ introSlide < 4 ? 'Next' : 'Get Started' }}
            </v-btn>
          </div>
        </div>
      </v-dialog>
    </v-overlay>
  <div
    id="main-content"
  > 
  <marquee-alert 
    v-if="smallSize && showExtendedRangeFeatures && extendedRangeAvailable" 
    :timeout="30000"
    message="You can view data with an extend range for the 
            duration of the LA fires. See the 🔥 button on the map"
    />
  <!-- add an alert if the last timestamps is more than 2 days behind -->
  <marquee-alert
    v-model="dataLagWarning"
    style="font-size:0.9em; gap: 1em;"
    icon="mdi-calendar-alert"
    type="error"
    :timeout="10000"
    fixed
  >
    {{ new Date(timestamps[timestamps.length-1]).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) }} is the latest date with available data. See <a href="https://asdc.larc.nasa.gov/project/TEMPO/" target="_blank" rel="noopener noreferrer">NASA's EarthData website</a> and <a href="https://github.com/Smithsonian/TEMPO-Observations-log/blob/main/daily_log.md"  target="_blank" rel="noopener noreferrer">TEMPO Observing Log</a> for more information.
    </marquee-alert>
    <div class="content-with-sidebars">
      <!-- tempo logo -->
      <div id="logo-title">
      <a href="https://tempo.si.edu" target="_blank" rel="noopener noreferrer" >
        <img 
          src="./assets/TEMPO-Logo-Small.png"
          alt="TEMPO Logo"
        >
      </a>

      <h1 id="title">What is in the Air You Breathe?</h1>
      <!-- </div> -->
      <cds-dialog title="What's new" v-model="showChanges" :color="accentColor2">
        <ul class="snackbar-alert-ul">
          <li class="change-item mb-5" v-for="change in changes" :key="change.date" :data-date="change.date">
            <span :style='{"font-weight":"bold", "color": `${change.highlight ? "var(--smithsonian-yellow)" : "currentColor"}` }'>{{ change.date }}</span><br> <span v-html="change.html">  </span>{{ change.text }}
          </li>
        </ul>
        <!-- <template v-slot:activator="{ onClick, id }">
          <v-btn :id="id" @click="onClick" color="primary">
            Custom Activator
          </v-btn>
        </template>  -->
      </cds-dialog>

      <div id="menu-area">
        <v-btn 
          v-if="(new Date('2026-07-25 00:00:00') > new Date())"
          class='whats-new-button pulse' 
          aria-label="What's new" 
          @click="showChanges = true" 
          @keyup.enter="showChanges = true" 
          variant="outlined" 
          rounded="lg" 
          :color="accentColor2" 
          elevation="0"
          size="small"
          >
          <v-tooltip location="bottom" activator="parent" :disabled="mobile" text="What's new"></v-tooltip>
          <v-icon>mdi-creation</v-icon>
        </v-btn>
        <share-button
            :source="currentUrl"
            buttonColor="black"
            :iconColor="accentColor2"
            elevation="0"
            size="small"
            rounded="1"
            :tooltip-disabled="mobile"
            @share="shareButtonClickedCount += 1"
            alert
          />
        <v-btn aria-role="menu" aria-label="Show menu" size="small" class="menu-button" variant="outlined" rounded="lg" :color="accentColor2" elevation="5">
          <v-icon size="x-large">mdi-menu</v-icon>
          <v-menu
            activator="parent"
            >
            <v-list>
              <v-list-item 
                tabindex="0"
                aria-label="See recent changes"
                @click="showChanges = true"
                @keyup.enter="showChanges = true"
                >
                What's New
              </v-list-item>

              <v-list-item 
                tabindex="0" 
                aria-label="Show introduction"
                @click="() => {introSlide = 1; inIntro = true;}"
                @keyup.enter="() => {introSlide = 1; inIntro = true;}"
                >
                  Introduction
              </v-list-item>
              
              <v-list-item 
                tabindex="0"
                aria-label="Show user guide"
                @click="() => {introSlide = 4; inIntro = true;}"
                @keyup.enter="() => {introSlide = 4; inIntro = true;}"
                >
                User Guide
              </v-list-item>
              
              <v-list-item 
                tabindex="0"
                aria-label="Show dialog telling about the data"
                @click="showAboutData = true"
                @keyup.enter="showAboutData = true"
                >
                About the Data
              </v-list-item>
              
              <v-list-item 
                
                aria-label="Leave Page to Educator Resources"
                >
                <a style="font-weight: normal;" tabindex="0"  href="https://www.cosmicds.cfa.harvard.edu/resources/tempo" target="_blank" rel="noopener">Educator Resources<v-icon>mdi-open-in-new</v-icon></a>
              </v-list-item>
              
              <v-list-item 
                tabindex="0" 
                aria-label="Show credits"
                @click="showCredits = true"
                @keyup.enter="showCredits = true"
                >
                  Credits
              </v-list-item>
              
            </v-list>
          </v-menu>
        </v-btn>
      </div>
    </div>
      <div id="where" class="big-label">where</div>
      <div id="map-container">
        <colorbar-horizontal
          v-if="display.width.value <= 750"
          label="Amount of NO2"
          backgroundColor="transparent"
          :nsteps="255"
          :cmap="cmapNO2"
          start-value="1"
          :end-value="showingExtendedRange ? '300' : '150'"
          :extend="true"
        >
        <template v-slot:label>
              <div style="text-align: center;">Amount of NO&#x2082;&nbsp;<span class="unit-label">(10&sup1;&#x2074; mol/cm&sup2;)</span></div>
        </template>
        </colorbar-horizontal>
        <div id="map-contents" style="width:100%; height: 100%;">
          <div id="map">
            <v-overlay
              :modelValue="loadedImagesProgress < 100"
              style="z-index: 1000;"
              class="align-center justify-center"
              contained
              opacity=".8"
              >
              <div id="loading-circle-progress-container" class="d-flex flex-column align-center justify-center ga-2">
                <label class="text-black" for="loading-circle-progress">Loading Data...</label>
                <v-progress-circular
                  id="loading-circle-progress"
                  style="z-index: 1000;"
                  :size="100"
                  :width="15"
                  :model-value="loadedImagesProgress"
                  color="#092088"
                >
                {{ loadedImagesProgress.toFixed(0) }}%
              </v-progress-circular>
              </div>
            </v-overlay>
          </div>

          <div v-if="showFieldOfRegard && !smallSize" id="map-legend"><hr class="line-legend">TEMPO Field of Regard</div>
          <!-- show hide cloud data, disable if none is available -->

          <v-menu
            id="map-controls"
            v-model="showControls"
            :close-on-content-click="false"
          >
            <template v-slot:activator="{ props }">
              <div id="map-show-hide-controls">
                <v-btn
                  v-bind="props"
                  :class="['mx-2', smallSize ? 'mt-0' : 'mt-5']"
                  elevation="2"
                  color="white"
                  icon
                  style="outline: 2px solid #b6b6b6;"
                  rounded="0"
                  size="x-small"
                >
                  <template v-slot:default>
                    <v-icon
                      color="black"
                      size="x-large"
                    >mdi-tune-variant</v-icon>
                  </template>
                </v-btn>
              </div>
            </template>
            <v-card class="controls-card">
              <font-awesome-icon
                style="position:absolute;right:16px;cursor:pointer"
                icon="square-xmark"
                size="xl"
                @click="showControls = false"
                @keyup.enter="showControls = false"
                :color="accentColor2"
                tabindex="0"
              ></font-awesome-icon>
              <div
                id="opacity-slider-container"
                class="mt-5"
              >
                <div id="opacity-slider-label">TEMPO data opacity</div>
                <v-slider
                    v-model="opacity"
                    :min="0"
                    :max="1"
                    color="#c10124"
                    density="compact"
                    hide-details
                    class="mb-4"
                    @end="onOpacitySliderEnd"
                  >
                </v-slider>
              </div>
              <div
                class="d-flex flex-row align-center justify-space-between"
              >
                <v-checkbox
                  v-model="showRoads"
                  @keyup.enter="showRoads = !showRoads"
                  label="Show Roads"
                  color="#c10124"
                  hide-details
                />
              </div>
              <div
                class="d-flex flex-row align-center justify-space-between"
              >
                <v-checkbox
                  v-model="showFieldOfRegard"
                  @keyup.enter="showFieldOfRegard = !showFieldOfRegard"
                  label="TEMPO Field of Regard"
                  color="#c10124"
                  hide-details
                />
                <info-button>
                  <p>
                    The TEMPO satellite observes the atmosphere over North America, from the Atlantic Ocean to the Pacific Coast, and from roughly Mexico City to central Canada. 
                  </p>
                  <p>
                    The TEMPO Field of Regard (in <span class="text-red">red</span>, currently <em>{{ showFieldOfRegard ? 'visible' : "hidden" }}</em>)
                    is the area over which the satellite takes measurements. 
                  </p>
                  </info-button>
                </div>
                <div class="d-flex flex-row align-center justify-space-between">
                <v-checkbox
                  v-model="showClouds"
                  @keyup.enter="showClouds = !showClouds"
                  :disabled="!cloudDataAvailable"
                  :label="cloudDataAvailable ? 'Show Cloud Mask' : 'No Cloud Data Available'"
                  color="#c10124"
                  hide-details
                />
                <info-button>
                  <p>
                    The cloud mask shows where the satellite could not measure NO<sub>2</sub> because of cloud cover. 
                  </p>
                </info-button>
              </div>
                <div class="d-flex flex-row align-center justify-space-between">
                  <v-checkbox
                  :disabled="!highresAvailable"
                  v-model="useHighRes"
                  @keyup.enter="useHighRes = !useHighRes"
                  label="Use High Resolution Data"
                  color="#c10124"
                  hide-details
                />
                <info-button>
                  <p>
                    By default we show data at 1/2 resolution for performance. 
                  </p>
                </info-button>
              </div>
                <div v-if="showExtendedRangeFeatures && extendedRangeAvailable" class="d-flex flex-row align-center justify-space-between">
                  <v-checkbox
                  :disabled="!extendedRangeAvailable"
                  v-model="showExtendedRange"
                  @keyup.enter="showExtendedRange = !showExtendedRange"
                  label="Use Extended NO2 range"
                  color="#c10124"
                  hide-details
                />
                <info-button>
                  <p>
                    If data is available, show map with extended range of NO<sub>2</sub> values.
                  </p>
                </info-button>
              </div>
            </v-card>
          </v-menu>
          <div id="location-and-sharing">
          <location-search
            v-model="searchOpen"
            small
            stay-open
            buttonSize="sm"
            persist-selected
            :search-provider="geocodingInfoForSearchLimited"
            @set-location="setLocationFromSearch"
            @error="(error: string) => searchErrorMessage = error"
          ></location-search>
        </div>
        
        <div id="la-fires">
          <v-btn v-if="!smallSize && extendedRangeAvailable" @click="activateLAViewer" @keyup.enter="activateLAViewer" >
            {{ extendedRangeAvailable ? (showExtendedRange ? "Showing extended range" : "Use extreme events range") : "No extended range images available for this date" }}
          </v-btn>
          <v-btn v-if="smallSize && showExtendedRangeFeatures && extendedRangeAvailable" @click="activateLAViewer" @keyup.enter="activateLAViewer" icon >
            🔥
          </v-btn>
          <cds-dialog title="Extreme Events" v-model="showLADialog" :color="accentColor2">
            <v-row>
              <v-col>
                <p>
                  Some events like the January 2025 Los Angeles fires generate so much smoke 
                  and pollution that NO<sub>2</sub> levels can greatly
                    exceed the default range used in the color scale 
                    for the TEMPO-lite viewer. To show more clearly where 
                    the very highest levels of NO<sub>2</sub> are present, 
                    you can use an extended color stretch.   
                </p>
                <p>
                  By default we display values from 0.01-1.5&times;10<sup>16</sup> molecules per square centimeter, 
                  check the box here to double the max of the range to 3&times;10<sup>16</sup> molecules per square centimeter. 
                  The extended range will be available on dates with extreme events like the Los Angeles fire outbreaks.
                </p>
            
            <v-checkbox v-model="showExtendedRange" label="Use extended data range"/>
            
            <!-- Note on clouds. Some times (such as January 19th) smoke is detected as "clouds" and so those pixels get removed. 
            We (CosmicDS) currently do not have an algorithmic way retrieve the nitrogen dioxide column in these cases and so 
            those pixels are masked out in the "cloud mask". -->
        
              </v-col>
            </v-row>
            <v-row class="justify-center">

              <v-btn :color="accentColor2" @click="goToLA" @keyup.enter="goToLA"> Go To Los Angeles</v-btn>

            </v-row>
          </cds-dialog>
        </div>
        
        </div>
        <colorbar 
          v-if="display.width.value > 750"
          label="Amount of NO2"
          backgroundColor="transparent"
          :nsteps="255"
          :cmap="cmapNO2"
          start-value="1"
          :end-value="showingExtendedRange ? '300' : '150'"
          :extend="true"
        >
          <template v-slot:label>
              <div style="text-align: center;">Amount of NO&#x2082;<br><span class="unit-label">(10&sup1;&#x2074; molecules/cm&sup2;)</span></div>
          </template>
        </colorbar>
        

      </div>
        <div id="when" class="big-label">when</div>
        <div id="slider-row">
          <v-slider
            :class='[
              "time-slider", 
              maxIndex <= minIndex ? "hide-first-last-ticks" : "",
              maxIndex < minIndex + 7 ? `hide-ticks-${minIndex + 7 - maxIndex}` : "",
              ]'
            :disabled="maxIndex <= minIndex"
            v-model="timeSliderIndex"
            :min="minIndex"
            :max="Math.max(minIndex + 7,maxIndex)"
            :step="1"
            color="#068ede95"
            thumb-label="always"
            :track-size="10"
            show-ticks="always"
            hide-details
            @end="onTimeSliderEnd"
          >
            <template v-slot:thumb-label>
              <div class="thumb-label">
                {{ thumbLabel }}
              </div>
            </template>
          </v-slider>
          <icon-button
            id="play-pause"
            :fa-icon="playing ? 'pause' : 'play'"
            :color="minIndex >= maxIndex ? '#AAAAAA' : 'var(--accent-color)'"
            :focus-color="minIndex >= maxIndex ? '#AAAAAA' : 'var(--accent-color)'"
            tooltip-location="bottom"
            fa-size="sm"
            @activate="() => {
              if (maxIndex <= minIndex) {
                handlePlayWithoutData();
                return;
              } 
              playing = !playing
              }"
          >
          </icon-button>
          <v-snackbar
            v-model="showSinglePointWarning"
            :timeout="5000"
            color="error"
            location="bottom"
            >
            <span style="font-weight: 600">Only one time is available for this date.</span>
            <template v-slot:actions>
              <v-btn
                color="black"
                variant="text"
                @click="showSinglePointWarning = false"
              >
                <span style="font-weight: 600">Close</span>
              </v-btn>
            </template>
          </v-snackbar>
        </div>

        <div id="user-options">
        <div id="all-dates">
          <h2>
          Select a Date
            <!-- warning about data being out of date -->
            <v-tooltip
              v-if="timestampsLoaded && (new Date(timestamps[timestamps.length-2]) < new Date(Date.now() - 2 * 24 * 60 * 60 * 1000))"
              :disabled="touchscreen"
              text="Data Availability Alert"
              location="right"
              open-on-focus
              open-on-hover
            >
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  color="error"
                  @click="() => dataLagWarning = !dataLagWarning"
                  @keyup.enter="() => dataLagWarning = !dataLagWarning"
                  icon="mdi-calendar-alert"
                  variant="tonal"
                  size="default"
                  width="35px"
                  height="35px"
                ></v-btn>
              </template>
            </v-tooltip>
          </h2>  
          <div class="d-flex flex-row align-center">
            <v-radio-group v-model="radio">
              <date-picker
                ref="calendar"
                :model-value="singleDateSelected"
                @internal-model-change="(value: Date) => {
                  if (value != null && value.getTime() != singleDateSelected.getTime()) {
                    radio = null;
                    singleDateSelected = value;
                    calendar?.closeMenu();
                  }
                }"
                :allowed-dates="uniqueDays"
                :clearable="false"
                :enable-time-picker="false"
                :multi-dates="false"
                :transitions="false"
                :format="(date: Date | null) => date?.toDateString()"
                :preview-format="(date: Date | null) => date?.toDateString()"
                no-today
                dark
              >
                <template #action-buttons>
                  <button
                    class="dp__action_button dp__action-latest"
                    @click="() => singleDateSelected = uniqueDays[uniqueDays.length - 1]"
                    @keyup.enter="() => singleDateSelected = uniqueDays[uniqueDays.length - 1]"
                    :disabled="singleDateSelected === uniqueDays[uniqueDays.length - 1]"
                    elevation="0"
                    size="sm"
                  >
                    Latest
                </button>
                </template>
                <!-- <template #action-extra="{ selectCurrentDate }">
                
                </template> -->
              </date-picker>
            </v-radio-group>
          </div>        
          <!-- create a list of the uniqueDays -->
          <!-- <v-select
            :modelValue="singleDateSelected"
            :disabled="radio !== 0"
            :items="uniqueDays"
            item-title="title"
            item-value="value"
            label="Select a Date"
            @update:model-value="(e) => { singleDateSelected = e;}"
            hide-details
            variant="solo"
          ></v-select> -->
          <!-- add buttons to increment and decrement the singledateselected -->
          <div class="d-flex flex-row align-center my-2">
            <v-tooltip :disabled="touchscreen" text="Previous Date">
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  class="rounded-icon-wrapper"
                  @click="moveBackwardOneDay"
                  @keyup.enter="moveBackwardOneDay"
                  :disabled="singleDateSelected === uniqueDays[0]"
                  color="#009ade"
                  variant="outlined"
                  elevation="0"
                  size="md"
                >
                  <v-icon>mdi-chevron-double-left</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
            <v-spacer></v-spacer>
            <v-tooltip :disabled="touchscreen" text="Get Data for latest available day">
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  style="padding-inline: 4px;"
                  @click="() => singleDateSelected = uniqueDays[uniqueDays.length - 1]"
                  @keyup.enter="() => singleDateSelected = uniqueDays[uniqueDays.length - 1]"
                  :disabled="singleDateSelected === uniqueDays[uniqueDays.length - 1]"
                  color="#009ade"
                  variant="outlined"
                  elevation="0"
                  size="md"
                >
                  Latest Data
                </v-btn>
              </template>
            </v-tooltip>
            <v-spacer></v-spacer>
            <v-tooltip :disabled="touchscreen" text="Next Date">
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  class="rounded-icon-wrapper"
                  @click="moveForwardOneDay"
                  @keyup.enter="moveForwardOneDay"
                  :disabled="singleDateSelected === uniqueDays[uniqueDays.length - 1]"
                  color="#009ade"
                  variant="outlined"
                  elevation="0"
                  size="md"
                >
                  <v-icon>mdi-chevron-double-right</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
          </div>
          <v-progress-linear
            v-if="loadedImagesProgress < 101"
            v-model="loadedImagesProgress"
            color="#092088"
            height="20"
          >
          <span v-if="loadedImagesProgress < 100">Loading Data ({{ loadedImagesProgress.toFixed(0) }}%)</span>
          <span v-else>Data Loaded</span>
          </v-progress-linear>
          <!-- <v-switch label="LA fires" v-model="showExtendedRange" /> -->
        </div>

        <hr style="border-color: grey">


          <div id="date-radio">
            <!-- make a v-radio-group with 3 options -->
          <h2>Featured Topics</h2>
          <v-radio-group
            v-model="radio"
            row
          >
            <div v-for = "(event, index) in interestingEvents" :key="index" class="d-flex flex-row align-center">
              <v-radio
                :class="event.highlighted ? 'highlighted' : ''"
                :label="event.label"
                :value="index"
                @keyup.enter="radio = index"
              >
              </v-radio>
              <info-button>
                <div style="display: inline-block; margin: 0; padding: 0;" v-html="event.info"></div>
              </info-button>
            </div>
            
          </v-radio-group>
        </div>
        
        <hr style="border-color: grey;"  v-if="radio !== null ">
        
        <div id="locations-of-interest" v-if="radio !== null">
          <h3 class="mb-1">Example Cases</h3>
          <v-radio-group
            v-if="radio !== null"
            v-model="sublocationRadio"
            row
          >
          <div
            v-for="(loi, index) in locationsOfInterest[radio]" 
            v-bind:key="index" 
            class="sublocation-radio-wrapper d-flex flex-row align-center space-between">
            <v-radio
              class="sublocation-radio"
              :label="loi.text"
              :value="index"
              @keyup.enter="sublocationRadio = index"
            ></v-radio>
            <info-button>
              <p v-html="locationsOfInterestText[radio][index]"></p>
            </info-button>
          </div>
          </v-radio-group>
        </div>

        <hr style="border-color: grey;">
        <div id="bottom-options">
          <br>
          <v-select
            v-model="selectedTimezone"
            label="Timezone"
            :items="timezoneOptions"
            item-title="name"
            item-value="tz"
          ></v-select>
          <v-checkbox
            v-if="false"
            :disabled="!highresAvailable"
            v-model="useHighRes"
            @keyup.enter="useHighRes = !useHighRes"
            label="Use High Resolution Data"
            color="#c10124"
            hide-details
          />
          <v-checkbox
            v-if="showExtendedRangeFeatures && extendedRangeAvailable"
            :disabled="!extendedRangeAvailable"
            v-model="showExtendedRange"
            @keyup.enter="showExtendedRange = !showExtendedRange"
            label="Use Extended NO2 range"
            color="#c10124"
            hide-details
          />
        </div>
      </div>
      
      <div id="information">
      <article>
        <h2>TEMPO NO<sub>2</sub> Data</h2>
        <p>
          <a href="https://tempo.si.edu" target="_blank" rel="noopener noreferrer" >
          TEMPO</a>, a collaboration between the Smithsonian and NASA, is the first space-based probe to measure air pollution hourly over North America at neighborhood scales. NO<sub>2</sub> (nitrogen dioxide) is one of the pollutants detected by TEMPO. It is produced by wildfires and the burning of fossil fuels. NO<sub>2</sub> contributes to the formation of harmful ground-level ozone and toxic particulates in the air we breathe.
        </p>

          <div class="d-flex flex-row justify-space-between">
            <cds-dialog
              title="Credits"
              id="credits-dialog"
              v-model="showCredits"
              activator="parent"
              :scrim="false"
              location="center center"
              :color="accentColor2"
            >
              <h4 class="mb-2"><a href="https://tempo.si.edu/" target="_blank" rel="noopener noreferrer">TEMPO</a> Team Acknowledgments:</h4>
              <p>
                Caroline Nowlan, Aaron Naeger, and Erika Wright provided dates and featured events of interest in the TEMPO data.
              </p>
              <p>
                Xiong Liu provided the L3 version 2 TEMPO data files.
              </p>
              <p>
                Heesung Chong provided the shape file for the TEMPO field of regard.
              </p>

              <p class="my-3">NASA's Scientific Visualization Studio provided the TEMPO NO<sub>2</sub> colormap.</p>

              <h4 class="mb-2"><a href="https://www.cosmicds.cfa.harvard.edu/" target="_blank" rel="noopener noreferrer">CosmicDS</a> Team:</h4> 

              John Lewis<br>
              Jonathan Foster<br>
              Pat Udomprasert<br>
              Jon Carifio<br>
              Alyssa Goodman<br>
              Erika Wright<br>
              Mary Dussault<br>
              Harry Houghton<br>
              Evaluator: Sue Sunbury<br>

              <funding-acknowledgement class="my-3"></funding-acknowledgement>
            </cds-dialog>

          <cds-dialog
            id="user-guide-dialog"
            v-model="showUserGuide"
            :scrim="false"
            location="center center"
            title="User Guide"
            :color="accentColor2"
          >
            <p>
              Do consectetur consequat dolore esse nulla .
            </p>

            <p>
              Reprehenderit sint ipsum laborum in reprehenderit sunt eu pariatur ipsum tempor .
            </p>

            <p>
              Ex laboris fugiat ad duis eu ipsum cupidatat veniam fugiat .
            </p>
          </cds-dialog>
          
          
          <cds-dialog
            id="about-data-dialog"
            v-model="showAboutData"
            :scrim="false"
            location="center center"
            title="Data source and processing"
            short-title="About Data"
            :color="accentColor2"
          >
            <p>
              This visualization of the TEMPO satellite NO<sub>2</sub> Tropospheric Column Density data is derived from Level 3 data files obtained from the 
              <a href="https://asdc.larc.nasa.gov/project/TEMPO" target="_blank" rel="noopener noreferrer">NASA ASDC TEMPO Data Products Page</a>.
            </p>
            <br />
            <p>
              The data has been processed and visualized by the CosmicDS team at the Harvard-Smithsonian Center for Astrophysics. The images displayed have undergone pre-processing to filter out erroneous data, and a 50% cloud cover mask has been applied. 
              For performance optimization, the data resolution has been halved and reprojected to a Web Mercator projection to ensure compatibility with 
              <a href="https://leafletjs.com/" target="_blank" rel="noopener noreferrer">Leaflet.js</a>.
            </p>
            <br />
            <p>
              The data is rendered using the color map provided by NASA's Scientific Visualization Studio.
            </p>
            <br />
            <p>
              All data processing scripts are available on 
              <a href="https://github.com/johnarban/tempo_processing_scripts" target="_blank" rel="noopener noreferrer">GitHub</a>.
            </p>
          </cds-dialog>

          <!-- make small inline show introduction link button -->
          <!-- <a href="#" @click="inIntro = true" @keyup.enter="inIntro = true" style="right: 0;">
            Show Introduction
          </a> -->
        </div>

      </article>
      </div>
      
    </div>
    <div id="body-logos">
      <a href="https://www.si.edu/" target="_blank" rel="noopener noreferrer" class="mr-1" 
      ><img alt="Smithsonian Logo" src="./assets/smithsonian.png"
        /></a>
      <credit-logos
         :default-logos="['cosmicds', 'sciact', 'nasa']"
       />
    </div>
  </div>

   <!-- Data collection opt-out dialog -->
    <v-dialog
      scrim="false"
      v-model="showPrivacyDialog"
      max-width="400px"
      id="privacy-popup-dialog"
    >
      <v-card>
        <v-card-text>
          To evaluate usage of this app, <strong>anonymized</strong> data may be collected, including locations viewed and map quiz responses. "My Location" data is NEVER collected.
        </v-card-text>
        <v-card-actions class="pt-3">
          <v-spacer></v-spacer>
          <v-btn
            color="#BDBDBD"
            href="https://www.cfa.harvard.edu/privacy-statement"
            target="_blank"
            rel="noopener noreferrer"
          >
          Privacy Policy
          </v-btn>
          <v-btn
            color="#ff6666"
            @click="() => {
              responseOptOut = true;
              showPrivacyDialog = false;
            }"
          >
          Opt out
          </v-btn>
          <v-btn 
            color="green"
            @click="() => {
              responseOptOut = false;
              showPrivacyDialog = false;
            }"
          >
            Allow
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <v-container>
    <v-expand-transition>
      <user-experience
        id="rating-root"
        v-if="showRating"
        :question="question"
        icon-size="3x"
        @dismiss="(_rating: UserExperienceRating | null, _comments: string | null) => {
          showRating = false;
        }"
        @rating="(rating: UserExperienceRating | null) => {
          currentRating = rating;
          updateUserExperienceInfo(currentRating, currentComments);
        }"
        @finish="(rating: UserExperienceRating | null, comments: string | null) => {
          currentRating = rating;
          currentComments = comments;
          updateUserExperienceInfo(currentRating, currentComments)
          showRating = false;
        }"
      >
        <template #footer>
          <div id="user-experience-footer">
            <v-btn
              class="rating-opt-put"
              color="#BDBDBD"
              size="small"
              variant="text"
              @click="() => {
                showRating = false;
                ratingOptOut = true;
              }"
            >
            Don't show again
            </v-btn>
            <v-btn
              class="privacy-button"
              color="#BDBDBD"
              @click="showRatingPrivacyPolicy = true"
              @keyup.enter="showRatingPrivacyPolicy = true"
              size="small"
              target="_blank"
              rel="noopener noreferrer"
            >
            What is this?
            </v-btn> 
          </div>
        </template>
      </user-experience>
    </v-expand-transition>
    <cds-privacy-policy v-model="showRatingPrivacyPolicy" />
  </v-container>
</v-app>
</template>
  
<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick, ComputedRef } from "vue";
import { API_BASE_URL, blurActiveElement, type UserExperienceRating } from "@cosmicds/vue-toolkit";
import { useDisplay } from 'vuetify';
import { DatePickerInstance } from "@vuepic/vue-datepicker";
import { v4 } from "uuid";
import { getTimezoneOffset } from "date-fns-tz";
import { cbarNO2 } from "./revised_cmap";
import { MapBoxFeature, MapBoxFeatureCollection, geocodingInfoForSearch } from "./mapbox";
import { _preloadImages } from "./PreloadImages";
import changes from "./changes";
import { useBounds } from './composables/useBounds';
import { interestingEvents } from "./interestingEventsAsList";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { LatLngPair, LngLatPair, InitMapOptions } from "./types";

import { useUniqueTimeSelection } from "./composables/useUniqueTimeSelection";


// Import Leaflet Composables
import { useMap } from "./composables/leaflet/useMap";
import { usezoomhome} from './composables/leaflet/useZoomHome';
import { useImageOverlay } from "./composables/leaflet/useImageOverlay";
import { useFieldOfRegard} from "./composables/leaflet/useFieldOfRegard";
import { useLocationMarker } from "./composables/leaflet/useMarker";
const zoomScale = 1; 

// const zoomScale = 0.5; // for matplibre-gl


const display = useDisplay();
const calendar = ref<DatePickerInstance | null>(null);
type SheetType = "text" | "video" | null;
type Timeout = ReturnType<typeof setTimeout>;


/**************
 * UI Setup
 *************/
// const hash = window.location.hash;
const urlParams = new URLSearchParams(window.location.search);
const showExtendedRangeFeatures = true; //hash.includes("extreme-events");
const showSplashScreen = ref(new URLSearchParams(window.location.search).get("splash")?.toLowerCase() !== "false");
const extendedRange = ref(window.location.hash.includes("extreme-events") || urlParams.get('extendedRange') === "true"); //showExtendedRangeFeatures || urlParams.get('extendedRange') === "true";
const hideIntro = urlParams.get("hideintro") === "true";
const WINDOW_DONTSHOWINTRO = hideIntro ? true : window.localStorage.getItem("dontShowIntro") === 'true';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore --> msPointerEnabled may not be defined in window.navigator
const touchscreen = ref(('ontouchstart' in window) || ('ontouchstart' in document.documentElement) || !!window.navigator.msPointerEnabled);



const STORY_VISIT_URL = `${API_BASE_URL}/tempo-lite/visit`;
function pingServer(venue: string) {

  fetch(STORY_VISIT_URL, {
    
    method: "POST",
    
    headers: {
      "Content-Type": "application/json",
      // eslint-disable-next-line @typescript-eslint/naming-convention
      "Authorization": process.env.VUE_APP_CDS_API_KEY ?? ''
    },
    
    body: JSON.stringify({
      info: { venue }
    })
    
  })
    .then((response) => {
      if (!response.ok) {
        console.error("Error pinging server", response.status, response.statusText);
      } 
    }).
    catch((error) => {
      console.error("Error pinging server", error);
    });
}

const venue = (urlParams.get("venue") ?? '').replace(/,/g, '-');

if (venue !== '') {
  // get current value of venue
  const venues =  window.localStorage.getItem("venues") ?? '';
  // if they haven't visited this venue before, ping the server & save it
  if (!venues.includes(venue)) {
    pingServer(venue);
    window.localStorage.setItem("venues", venues ? `${venues},${venue}` : venue);
  }
}


function clearUrl(hash = false) {
  const newUrl = location.origin + location.pathname + (hash ? location.hash : '');
  window.history.replaceState({}, '', newUrl);
}
clearUrl(true);


const introSlide = ref(1);
const inIntro = ref(!WINDOW_DONTSHOWINTRO);
const dontShowIntro = ref(WINDOW_DONTSHOWINTRO);
const showNotice = ref(true);
const sheet = ref<SheetType>(null);
const accentColor = ref("#068ede");
const accentColor2 = ref("#ffcc33");

let hasOptOutBeenOpened = false;
const introductionOpen = computed(() => inIntro.value && (introSlide.value === 1));
const userGuideOpen = computed(() => inIntro.value && (introSlide.value === 4));

let whatsNewOpenedCount = 0;
let whatsNewTimestamp = null as number | null;
let whatsNewOpenTimeMs = 0;
let introductionOpenedCount = 0;
let introductionTimestamp = null as number | null;
let introductionOpenTimeMs = 0;
let userGuideOpenedCount = 0;
let userGuideTimestamp = null as number | null;
let userGuideOpenTimeMs = 0;
let aboutDataOpenedCount = 0;
let aboutDataTimestamp = null as number | null;
let aboutDataOpenTimeMs = 0;
let creditsOpenedCount = 0;
let creditsTimestamp = null as number | null;
let creditsOpenTimeMs = 0;
let shareButtonClickedCount = 0;
let playButtonClickedCount = 0;
let timeSliderUsedCount = 0;
let opacitySliderUsedCount = 0;
let fieldOfRegardToggled = false;
let cloudMaskToggled = false;
let hiResDataToggled = false;


let userSelectedCalendarDates: number[] = [];
let userSelectedTimezones: string[] = [];
let userSelectedLocations: string[] = [];
let userSelectedNotableEvents: [string, string][] = [];

const STORY_DATA_URL = `${API_BASE_URL}/tempo-lite/data`;
const OPT_OUT_KEY = "tempo-lite-optout" as const;
const STORY_RATING_URL = `${API_BASE_URL}/tempo-lite/user-experience`;
const RATING_OPT_OUT_KEY = "tempo-lite-optout" as const;

const storedRatingOptOut = window.localStorage.getItem(RATING_OPT_OUT_KEY);

const showRatingPrivacyPolicy = ref(false);
const question = Math.random() > 0.5 ? 
  "Does this spark your curiosity?" :
  "Are you learning something new?";
const currentRating = ref<UserExperienceRating | null>(null);
const currentComments = ref<string | null>(null);

const UUID_KEY = "tempo-lite-uuid" as const;
const storedOptOut = window.localStorage.getItem(OPT_OUT_KEY);
const maybeUUID = window.localStorage.getItem(UUID_KEY);
const optOut = typeof storedOptOut === "string" ? storedOptOut === "true" : null;
const showPrivacyDialog = ref(false);
const responseOptOut = ref(optOut);

// If the user has opted out of all data collection, don't ask them the question
const ratingOptedOut = typeof storedRatingOptOut === "string" ? storedRatingOptOut === "true" : null;
const ratingOptOut = ref(responseOptOut.value || ratingOptedOut);
let ratingTimeout: ReturnType<typeof setTimeout> | null = null;
const showRating = ref(false);

const existingUser = maybeUUID !== null;
const uuid = maybeUUID ?? v4();
if (!existingUser) {
  window.localStorage.setItem(UUID_KEY, uuid);
}


function updateUserExperienceInfo(rating: UserExperienceRating | null, comments: string | null) {
  const body: Record<string, unknown> = {
    uuid,
    question,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    story_name: "tempo-lite",
  };
  if (rating) {
    body.rating = rating;
  }
  if (comments) {
    body.comments = comments;
  }
  fetch(STORY_RATING_URL, {
    method: "PUT",
    headers: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      "Authorization": process.env.VUE_APP_CDS_API_KEY ?? "",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
}



function clearRatingTimeout() {
  if (ratingTimeout !== null) {
    clearTimeout(ratingTimeout);
    ratingTimeout = null;
  }
}


function selectSheet(name: SheetType) {
  if (sheet.value === name) {
    sheet.value = null;
    nextTick(() => {
      blurActiveElement();
    });
  } else {
    sheet.value = name;
  }
}

const showTextSheet = computed({
  get() {
    return sheet.value === 'text';
  },
  set(_value: boolean) {
    selectSheet('text');
  }
});

const cssVars = computed(() => {
  return {
    '--accent-color': accentColor.value,
    '--accent-color-2': accentColor2.value,
    '--app-content-height': showTextSheet.value ? '66%' : '100%',
  };
});

const smallSize = computed(() => {
  return display.smAndDown.value;
});

const mobile = computed(() => {
  return smallSize.value && touchscreen.value;
});


function zpad(n: number, width: number = 2, character: string = "0"): string {
  return n.toString().padStart(width, character);
}

/************
 * TIMESTAMP SETUP
 ************/
import { getTimestamps, getExtendedRangeTimestamps } from "./timestamps";

const erdTimestamps = ref<number[]>([]);
const newTimestamps = ref<number[]>([]);
const cloudTimestamps = ref<number[]>([]);
const fosterTimestamps = ref<number[]>([
  1698838920000,
  1698841320000,
  1698843720000,
  1698846120000,
  1698848520000,
  1698852120000,
  1698855720000,
  1698859320000,
  1698862920000,
  1698866520000,
  1698870120000,
  1698873720000,
  1698876120000,
  1698878520000,
  1698880920000,
  1699011720000,
  1699014120000,
  1699016520000,
  1699018920000,
  1699021320000,
  1699024920000,
  1699028520000,
  1699032120000,
  1699035720000,
  1699039320000,
  1699042920000,
  1699046520000,
  1699048920000,
  1699051320000,
  1699053720000,
  1711626180000,
  1711628640000,
  1711631040000,
  1711633440000,
  1711637040000,
  1711640640000,
  1711644240000,
  1711647840000,
  1711651440000,
  1711655040000,
  1711658640000,
  1711662240000,
  1711665840000,
  1711668240000,
]);


const timestamps = ref<number[]>(fosterTimestamps.value);
const extendedRangeTimestamps = ref<number[]>([]);
const showExtendedRange = ref(extendedRange.value);
const useHighRes = ref(false);
const {
  timeIndex,
  timestamp,
  date,
  singleDateSelected,
  maxIndex,
  minIndex,
  uniqueDays,
  uniqueDaysIndex,
  setNearestDate,
  moveBackwardOneDay,
  moveForwardOneDay,
  nearestDateIndex } = useUniqueTimeSelection(timestamps);
  
const timeSliderIndex = computed({
  get() {
    return timeIndex.value;
  },
  set(value: number) {
    if (value > maxIndex.value) {
      return;
    }
    timeIndex.value = value;
  }
});

const timestampsLoaded = ref(false);
const fosterTimestampsSet = ref(new Set(fosterTimestamps.value));
const erdTimestampsSet = ref(new Set());
const newTimestampsSet = ref(new Set());
const cloudTimestampsSet = ref(new Set());
const extendedRangeTimestampsSet = ref(new Set());
const timestampsSet = ref(new Set(fosterTimestamps.value));

async function updateTimestamps() {
  return Promise.all([
    getExtendedRangeTimestamps().then(ts => {
      extendedRangeTimestamps.value = ts;
      extendedRangeTimestampsSet.value = new Set(ts);
    }),
    getTimestamps().then((ts) => {
      erdTimestamps.value = ts.early_release;
      erdTimestampsSet.value = new Set(ts.early_release);
      newTimestamps.value = ts.released;
      newTimestampsSet.value = new Set(ts.released);
      timestamps.value = timestamps.value.concat(erdTimestamps.value, newTimestamps.value).sort();
      timestampsSet.value = new Set(timestamps.value);
      cloudTimestamps.value = ts.clouds;
      cloudTimestampsSet.value = new Set(ts.clouds);
    })
  ]);
}

updateTimestamps().then(() => { timestampsLoaded.value = true; })
  .then(() => {
    if (window.location.hash.includes("extreme-events")) {
      nextTick(() => {
        activateExtremeEvents();
      });
    }
  });


/************
 * INITIAL LOCATIONS SETUP
 ************/


const initLat = parseFloat(urlParams.get("lat") || '40.044');
const initLon = parseFloat(urlParams.get("lon") || '-98.789');
const initZoom = parseFloat(urlParams.get("zoom") || `${4 * zoomScale}`); // 4 is the default zoom level for the map, multiplied by zoomScale for maplibre
const initTime = urlParams.get("t");
const initState = ref<InitMapOptions>({
  loc: [initLat, initLon] as LatLngPair,
  zoom: initZoom,
  t: initTime ? +initTime : null
});


const homeLat = 40.044;
const homeLon = -98.789;
const homeZoom = 4 * zoomScale; // 4 is the default zoom level for the map, multiplied by zoomScale for maplibre
const homeState = ref({
  loc: [homeLat, homeLon] as LatLngPair,
  zoom: homeZoom,
  t: null as number | null
});


const radio = ref<number | null>(null);
const sublocationRadio = ref<number | null>(null);


// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { currentBounds: imageBounds } = useBounds(date);





const selectedTimezone = ref("US/Eastern");
const playing = ref(false);
const playInterval = ref<Timeout | null>(null);
const searchOpen = ref(true);
const searchErrorMessage = ref<string | null>(null);
const showControls = ref(false);
const showCredits = ref(false);
const showUserGuide = ref(false);
const showAboutData = ref(false);
const loadedImagesProgress = ref(0);

const showLocationMarker = ref(true);
const currentUrl = ref(window.location.href);
const showChanges = ref(false);
const showLADialog = ref(false);
const dataLagWarning = ref(false);


const imageName = computed(() => {
  return getTempoFilename(date.value);
});

const imageUrl = computed(() => {
  if (customImageUrl.value) {
    return customImageUrl.value;
  }
  const url = getTempoDataUrl(timestamp.value);
  if (url === null) { return ''; }
  return url + imageName.value;
});

const showClouds = ref(false);
const cloudUrl = computed(() => {
  if (!showClouds.value) {
    return '';
  }

  if (cloudTimestampsSet.value.has(timestamp.value)) {
    return getCloudFilename(date.value);
  }
  return '';
});


const opacity = ref(0.9);
const preload = ref(true);
const customImageUrl = ref("");
const cloudOverlay = useImageOverlay(cloudUrl, opacity, imageBounds, 'cloud-overlay');
const imageOverlay = useImageOverlay(imageUrl, opacity, imageBounds, 'image-overlay');

const cloudDataAvailable = computed(() => {
  return cloudTimestampsSet.value.has(timestamp.value);
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const whichDataSet = computed(() => {
  if (fosterTimestampsSet.value.has(timestamp.value)) {
    return 'TEMPO-lite';
  }

  if (erdTimestampsSet.value.has(timestamp.value)) {
    return 'Early Release (V01)';
  }

  if (newTimestampsSet.value.has(timestamp.value)) {
    return 'Level 3 (V03)';
  }

  return 'Unknown';
});


const highresAvailable = computed(() => {
  return newTimestampsSet.value.has(timestamp.value);
});

const extendedRangeAvailable = computed(() => {
  return extendedRangeTimestampsSet.value.has(timestamp.value);
});

const showingExtendedRange = computed(() => {
  return showExtendedRangeFeatures && showExtendedRange.value && extendedRangeAvailable.value;
});




const onMapReady = (map) => {
  map.on('moveend', updateURL);
  map.on('zoomend', updateURL);
};
const showRoads = ref(true);
const { map, createMap, setView } = useMap("map", initState.value, showRoads, onMapReady);

const { 
  addFieldOfRegard,
  showFieldOfRegard,
  updateFieldOfRegard 
} = useFieldOfRegard(singleDateSelected, map);

const {
  setMarker,
  removeMarker,
  locationMarker
} = useLocationMarker(map,  showLocationMarker.value);



onMounted(() => {
  window.addEventListener("hashchange", updateHash);
  showSplashScreen.value = false;
  createMap();

  usezoomhome(map, homeState.value.loc, homeState.value.zoom, (_e: Event) => {
    sublocationRadio.value = null;
    // check if location marker is not null and on map. if so remove it
    if (locationMarker.value !== null) {
      removeMarker();
    }
  });


  singleDateSelected.value = uniqueDays.value[uniqueDays.value.length - 1];
  if (map.value !== null) {
    imageOverlay.addTo(map.value); 
    cloudOverlay.addTo(map.value);
  }

  updateFieldOfRegard();
  addFieldOfRegard();

  createUserEntry();
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      updateUserData();
    } else {
      resetData();
    }
  });
  
});



const datesOfInterest = computed(() => {
  return interestingEvents.map(event => event.date);
});

const locationsOfInterest = computed(() => {
  return interestingEvents.map(event =>
    event.locations.map(loc => ({
      ...loc,
      index: nearestDateIndex(new Date(loc.time)),
    }))
  );
});

const locationsOfInterestText = computed(() => {
  return interestingEvents.map(event =>
    event.locations.map(loc => loc.description)
  );
});

const dateIsDST = computed(() => {
  const standardOffset = getTimezoneOffset(selectedTimezone.value, new Date(date.value.getUTCFullYear(), 0, 1));
  const currentOffset = getTimezoneOffset(selectedTimezone.value, date.value);
  if (standardOffset === currentOffset) {
    return false;
  }
  return true;
});

const timezoneOptions = computed(() => {
  return [
    { tz: 'US/Eastern', name: dateIsDST.value ? 'Eastern Daylight' : 'Eastern Standard' },
    { tz: 'US/Central', name: dateIsDST.value ? 'Central Daylight' : 'Central Standard' },
    { tz: 'US/Mountain', name: dateIsDST.value ? 'Mountain Daylight' : 'Mountain Standard' },
    { tz: 'US/Arizona', name: 'Mountain Standard' },
    { tz: 'US/Pacific', name: dateIsDST.value ? 'Pacific Daylight' : 'Pacific Standard' },
    { tz: 'US/Alaska', name: dateIsDST.value ? 'Alaska Daylight' : 'Alaska Standard' },
    { tz: 'UTC', name: 'UTC' },
  ];
});

// TODO: Maybe there's a built-in Date function to get this formatting?
const thumbLabel = computed(() => {
  const offset = getTimezoneOffset(selectedTimezone.value, date.value);
  const dateObj = new Date(timestamp.value + offset);
  const hours = dateObj.getUTCHours();
  const amPm = hours >= 12 ? "PM" : "AM";
  let hourValue = hours % 12;
  if (hourValue === 0) {
    hourValue = 12;
  }
  return `${date.value.getUTCMonth() + 1}/${dateObj.getUTCDate()}/${dateObj.getUTCFullYear()} ${hourValue}:${dateObj.getUTCMinutes().toString().padStart(2, '0')} ${amPm}`;
});




function updateHash() {
  if (window.location.hash.includes("extreme-events")) {
    activateExtremeEvents();
  }
}


function updateURL() {
  if (map.value) {
    const center = map.value.getCenter();
    let stateObj = null as Record<string, string> | null;
    if (showExtendedRangeFeatures) {
      stateObj = {
        lat: `${center.lat.toFixed(4)}`,
        lon: `${center.lng.toFixed(4)}`,
        zoom: `${map.value.getZoom()}`,
        t: `${timestamp.value}`,
        extendedRange: `${showExtendedRange.value}`
      };
    } else {
      stateObj = {
        lat: `${center.lat.toFixed(4)}`,
        lon: `${center.lng.toFixed(4)}`,
        zoom: `${map.value.getZoom()}`,
        t: `${timestamp.value}`
      };
    }
    const url = new URL(location.origin);
    const searchParams = new URLSearchParams(stateObj ?? {});
    // const hash = window.location.hash;
    // url.hash = hash;
    url.pathname = location.pathname;
    window.history.replaceState(null, '', url.toString());
    url.search = searchParams.toString();
    currentUrl.value = url.toString();
    // window.history.replaceState(stateObj, '', url);
  }
}

function cmapNO2(x: number): string {
  const rgb = cbarNO2(0, 1, x);
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]},1)`;
}

async function geocodingInfoForSearchLimited(searchText: string): Promise<MapBoxFeatureCollection | null> {
  return geocodingInfoForSearch(searchText, {
    countries: ["US", "CA", "MX", "CU", "BM", "HT", "DO"],
    limit: 10,
  }).catch(_err => null);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function resetMapBounds() {
  setView([40.044, -98.789] as LatLngPair, 4);
}

function setLocationFromSearch(items: [MapBoxFeature | null, string]) {
  const [feature, text] = items;
  if (feature !== null) {
    // Latitude, Longitude order
    const coordinates: LatLngPair = [feature.center[1], feature.center[0]] as LatLngPair;
    setView(coordinates, 12);
    setMarker(coordinates);
    userSelectedLocations.push(text);
  }
}

function play() {
  playInterval.value = setInterval(() => {
    if (timeIndex.value >= maxIndex.value) {
      if (playInterval.value) {
        // clearInterval(this.playInterval);
        // this.playInterval = null;
        // let it loop
        timeIndex.value = minIndex.value;
      }
    } else {
      timeIndex.value += 1;
    }
  }, 1000);
}


function pause() {
  if (playInterval.value) {
    clearInterval(playInterval.value);
  }
}

const showSinglePointWarning = ref(false);
function handlePlayWithoutData() {
  console.log('No data available for the selected time. Please select a different time.');
  playing.value = false;
  pause();
  showSinglePointWarning.value = true;
}

function getCloudFilename(date: Date): string {
  const filename = getTempoFilename(date);
  if (useHighRes.value) {
    return 'https://raw.githubusercontent.com/johnarban/tempo-data-holdings/main/clouds/images/' + filename;
  } else {
    return 'https://raw.githubusercontent.com/johnarban/tempo-data-holdings/main/clouds/images/resized_images/' + filename;
  }
}


function getTempoFilename(date: Date): string {
  return `tempo_${date.getUTCFullYear()}-${zpad(date.getUTCMonth() + 1)}-${zpad(date.getUTCDate())}T${zpad(date.getUTCHours())}h${zpad(date.getUTCMinutes())}m.png`;
}


function getTempoDataUrl(timestamp: number): string {
  if (showExtendedRange.value && extendedRangeTimestampsSet.value.has(timestamp)) {
    if (useHighRes.value) {
      return 'https://raw.githubusercontent.com/johnarban/tempo-data-holdings/main/data_range_0_300/released/images/';
    }
    return 'https://raw.githubusercontent.com/johnarban/tempo-data-holdings/main/data_range_0_300/released/images/resized_images/';
  }
  if (fosterTimestampsSet.value.has(timestamp)) {
    return 'https://tempo-images-bucket.s3.amazonaws.com/tempo-lite/';
  }

  if (erdTimestampsSet.value.has(timestamp)) {
    return 'https://raw.githubusercontent.com/johnarban/tempo-data-holdings/main/early_release/images/';
  }

  if (newTimestampsSet.value.has(timestamp)) {
    if (useHighRes.value) {
      return 'https://raw.githubusercontent.com/johnarban/tempo-data-holdings/main/released/images/';
    }
    return "https://raw.githubusercontent.com/johnarban/tempo-data-holdings/main/released/images/resized_images/";
  }

  return '';
}




function imagePreload() {
  if (!preload.value) {
    return;
  }
  // console.log('preloading images for ', this.thumbLabel);
  const times = timestamps.value.slice(minIndex.value, maxIndex.value + 1);
  const images = times.map(ts => getTempoDataUrl(ts) + getTempoFilename(new Date(ts)));
  const cloudImages = times.reduce((acc: string[], ts) => {
    if (cloudTimestampsSet.value.has(ts)) {
      acc.push(getCloudFilename(new Date(ts)));
    }
    return acc;
  }, []);
  images.push(...cloudImages);
  const promises = _preloadImages(images);
  let loaded = 0;
  loadedImagesProgress.value = 0;
  promises.forEach((promise) => {
    promise.then(() => {
      loaded += 1;
      loadedImagesProgress.value = (loaded / promises.length) * 100;
    }).catch((err) => {
      console.error('error loading image', err);
    });
  });
}

function within1Day(date1: Date, date2: Date): boolean {
  const diff = Math.abs(date1.getTime() - date2.getTime());
  return diff <= (24 * 60 * 60 * 1000);
}

function goToLocationOfInterst(index: number, subindex: number) {
  if (index < 0 || index >= locationsOfInterest.value.length) {
    console.warn('Invalid index for location of interest');
    return;
  }
  const loi = locationsOfInterest.value[index][subindex];
  const loiDay = new Date(loi.time);
  console.log(`Single date selected: ${singleDateSelected.value}, LOI day: ${loiDay}`);
  
  if (!within1Day(singleDateSelected.value, loiDay)) {
    singleDateSelected.value = uniqueDays.value[uniqueDaysIndex(loiDay.getTime())];
  }
  // need to wait for singleDateSelected to update before setting timeIndex
  nextTick(() => {
    // set time index to loi index
    if (loi.index !== undefined) {
      console.log(`Setting time index to ${loi.index} for LOI at ${loi.day}`);
      timeIndex.value = loi.index;
    } else {
      console.warn('No index found for location of interest');
    }
    setView(loi.latlng, loi.zoom * zoomScale); // Adjust zoom level for maplibre
  });

}


function goToLA() {
  showLADialog.value = false;
  const event = interestingEvents.filter(e => e.label == 'LA Wildfires (Jan 8, 2025)');
  if (event !== undefined && map.value) {
    const loi = event[0].locations;
    setView(loi[0].latlng, loi[0].zoom * zoomScale); // Adjust zoom level for maplibre
  }
}


function activateLAViewer() {
  showLADialog.value = true;
}


function activateExtremeEvents() {
  // Find the LA Wildfires event
  const laWildfireIndex = interestingEvents.findIndex(
    event => event.label?.includes("LA Wildfires")
  );

  if (laWildfireIndex !== -1) {
    // Set the radio to select this event
    radio.value = laWildfireIndex;
    // Make sure extended range is on
    showExtendedRange.value = true;
  }
}

function onTimeSliderEnd(_value: number) {
  timeSliderUsedCount += 1; 
}

function onOpacitySliderEnd(_value: number) {
  opacitySliderUsedCount += 1;
}

async function createUserEntry() {
  if (responseOptOut.value) {
    return;
  }

  const response = await fetch(`${STORY_DATA_URL}/${uuid}`, {
    method: "GET",
    // eslint-disable-next-line @typescript-eslint/naming-convention
    headers: { "Authorization": process.env.VUE_APP_CDS_API_KEY ?? "" }
  });
  const content = await response.json();
  const userExists = response.status === 200 && content.response?.user_uuid != undefined;
  
  if (!userExists) {
    fetch(`${STORY_DATA_URL}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        // eslint-disable-next-line @typescript-eslint/naming-convention
        "Authorization": process.env.VUE_APP_CDS_API_KEY ?? "",
      },
      body: JSON.stringify({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        user_uuid: uuid,
      }),
    });
  }
  
  if (ratingOptOut.value) {
    return;
  }
  
  // let gaveRating = false;
  // if (userExists) {
  //   try {
  //     const ratingResponse = await fetch(`${STORY_RATING_URL}/${uuid}`, {
  //       method: "GET",
  //       // eslint-disable-next-line @typescript-eslint/naming-convention
  //       headers: { "Authorization": process.env.VUE_APP_CDS_API_KEY ?? "" }
  //     });

  //     const ratingContent = await ratingResponse.json();
  //     gaveRating = ratingResponse.status === 200 && ratingContent.ratings?.length > 0;
  //   } catch (e) {
  //     console.error(e);
  //   }
  // }
  //
  // if (!gaveRating) {
  //   ratingTimeout = setTimeout(() => {
  //     showRating.value = true; 
  //   }, 60_000);
  // }
  
  
}

function resetData() {
  whatsNewOpenedCount = 0;
  introductionOpenedCount = 0;
  userGuideOpenedCount = 0;
  aboutDataOpenedCount = 0;
  creditsOpenedCount = 0;
  shareButtonClickedCount = 0;
  playButtonClickedCount = 0;
  timeSliderUsedCount = 0;
  opacitySliderUsedCount = 0;
  userSelectedCalendarDates = [];
  userSelectedTimezones = [];
  userSelectedLocations = [];
  userSelectedNotableEvents = [];

  aboutDataOpenTimeMs = 0;
  userGuideOpenTimeMs = 0;
  introductionOpenTimeMs = 0;
  whatsNewOpenTimeMs = 0;
  creditsOpenTimeMs = 0;
  fieldOfRegardToggled = false;
  cloudMaskToggled = false;
  hiResDataToggled = false;

  const now = Date.now();
  creditsTimestamp = showCredits.value ? now : null;
  aboutDataTimestamp = showAboutData.value ? now : null;
}

async function updateUserData() {
  if (responseOptOut.value) {
    return;
  }

  const now = Date.now();
  const creditsTime = (showCredits.value && creditsTimestamp !== null) ? now - creditsTimestamp : creditsOpenTimeMs;
  const aboutDataTime = (showAboutData.value && aboutDataTimestamp !== null) ? now - aboutDataTimestamp: aboutDataOpenTimeMs;
  const whatsNewTime = (showChanges.value && whatsNewTimestamp !== null) ? now  - whatsNewTimestamp : whatsNewOpenTimeMs;
  const introductionTime = (introductionOpen.value && introductionTimestamp !== null) ? now - introductionTimestamp : introductionOpenTimeMs;
  const userGuideTime = (userGuideOpen.value && userGuideTimestamp !== null) ? now - userGuideTimestamp : userGuideOpenTimeMs;

  fetch(`${STORY_DATA_URL}/${uuid}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      // eslint-disable-next-line @typescript-eslint/naming-convention
      "Authorization": process.env.VUE_APP_CDS_API_KEY ?? "",
    },
    body: JSON.stringify({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      delta_whats_new_opened_count: whatsNewOpenedCount,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      delta_whats_new_open_time_ms: whatsNewTime,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      delta_introduction_opened_count: introductionOpenedCount,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      delta_introduction_open_time_ms: introductionTime,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      delta_user_guide_opened_count: userGuideOpenedCount,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      delta_user_guide_open_time_ms: userGuideTime,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      delta_about_data_opened_count: aboutDataOpenedCount,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      delta_about_data_open_time_ms: aboutDataTime,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      delta_credits_opened_count: creditsOpenedCount,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      delta_credits_open_time_ms: creditsTime,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      delta_share_button_clicked_count: shareButtonClickedCount,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      user_selected_calendar_dates: userSelectedCalendarDates,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      user_selected_timezones: userSelectedTimezones,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      user_selected_locations: userSelectedLocations,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      user_selected_notable_events: userSelectedNotableEvents,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      delta_play_clicked_count: playButtonClickedCount,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      delta_time_slider_used_count: timeSliderUsedCount,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      delta_opacity_slider_used_count: opacitySliderUsedCount,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      field_of_regard_toggled: fieldOfRegardToggled,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      cloud_mask_toggled: cloudMaskToggled,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      high_res_data_toggled: hiResDataToggled,
    }),
    keepalive: true,
  }).then(() => {
    resetData();
  });
}

watch(timestampsLoaded, (loaded: boolean) => {
  if (loaded) {
    console.log('timestamps loaded');
    if (initState.value.t) {
      let index = uniqueDaysIndex(initState.value.t);
      if (index == -1) {
        return;
      }
      console.log('set the date');
      singleDateSelected.value = uniqueDays.value[index];
      index = nearestDateIndex(new Date(initState.value.t));
      if (index == -1) {
        return;
      }
      timeIndex.value = index;
      // FIXME if needed. if we find the time is not being set, use nextTick
      // this.$nextTick(() => { this.timeIndex = index;});
    }
  }
});

watch(timestamp, (_val: number) => {
  updateURL();
});

watch(introSlide, (val: number) => {
  inIntro.value = val < 5;
});

watch(dontShowIntro, (val: boolean) => {
  window.localStorage.setItem("dontShowIntro", val.toString());
  if (!val) {
    inIntro.value = true;
  }
});

watch(inIntro, (value: boolean) => {
  if (!value) {
    if (responseOptOut.value === null && !hasOptOutBeenOpened) {
      showPrivacyDialog.value = true;
    }
  }
});

watch(showPrivacyDialog, (show: boolean) => {
  if (show) {
    hasOptOutBeenOpened = true;
  }
});

watch(responseOptOut, (optOut: boolean | null) => {
  if (optOut !== null) {
    window.localStorage.setItem(OPT_OUT_KEY, String(optOut));
    if (optOut) {
      clearRatingTimeout();
      showRating.value = false;
    }
  }
});

watch(ratingOptOut, (optOut: boolean | null) => {
  if (optOut !== null) {
    window.localStorage.setItem(RATING_OPT_OUT_KEY, String(optOut));
    if (optOut) {
      clearRatingTimeout();
    }
  }
});

watch(loadedImagesProgress, (val: number) => {
  playing.value = false;
  const btn = document.querySelector('#play-pause-button');
  if (btn) {
    if (val < 100) {
      btn.setAttribute('disabled', 'true');
    } else {
      btn.removeAttribute('disabled');
    }
  }
});

watch(playing, (val: boolean) => {
  if (val) {
    play();
    playButtonClickedCount += 1;
  } else {
    pause();
  }
});

watch(imageUrl, (_url: string) => {
  updateFieldOfRegard();
});

// watch(cloudUrl, (_url: string) => {
//   // nothing to do here.
// });

watch(useHighRes, () => {
  hiResDataToggled = true;
  imagePreload();
});



watch(showFieldOfRegard, (_show: boolean) => {
  fieldOfRegardToggled = true;
});

watch(showClouds, (_show: boolean) => {
  cloudMaskToggled = true;
});

watch(showLocationMarker, (show: boolean) => {
  if (locationMarker.value) {
    if (show && map.value) {
      locationMarker.value.addTo(map.value);
      return;
    } else {
      locationMarker.value.remove();
      return;
    }
  }
});

watch(timestamps, () => {
  singleDateSelected.value = uniqueDays.value[uniqueDays.value.length - 1];
});

watch(radio, (value: number | null) => {
  // reset the sublocation radio
  sublocationRadio.value = null;
  
  if (value == null) {
    setNearestDate(singleDateSelected.value.getTime());
    return;
  }
  
  // is there a date for this event? then go to that date
  const date = datesOfInterest.value[value];
  if (date) {
    singleDateSelected.value = date;
  }
  // then go to the first location of interest for this event
  // nextTick(() => {
  //   sublocationRadio.value = 0;
  // });
});

watch(singleDateSelected, (value: Date) => {
  const timestamp = value.getTime();
  if (radio.value !== null) {
    const indices = datesOfInterest.value
      .map((d, i) => (d?.getTime() === timestamp ? i : -1))
      .filter(i => i !== -1);
    
    const sublocationIndices = locationsOfInterest.value[radio.value]
      .filter(loc => {
        const locTime = new Date(loc.time);
        return within1Day(locTime, value);
      });
    

    if (!indices.includes(radio.value) && sublocationIndices.length === 0) {
      const newRadioValue = indices.length > 0 ? indices[0] : null;
      if (newRadioValue !== radio.value) {
        radio.value = newRadioValue;
      }
    }
  }
  imagePreload();
});

watch(sublocationRadio, (value: number | null) => {
  if (value !== null && radio.value != null) {
    // nextTick so that singleDateSelected has time to update
    nextTick(() => goToLocationOfInterst(radio.value!, value));
  }
});


watch(showChanges, (_value: boolean) => {
  if (showNotice.value) {
    showNotice.value = false;
  }
});

watch(showExtendedRange, (_value: boolean) => {
  updateURL();
  imagePreload();
});

watch(showChanges, (show: boolean) => {
  const now = Date.now();
  if (show) {
    whatsNewTimestamp = now;
    whatsNewOpenedCount += 1;
  } else if (whatsNewTimestamp !== null) {
    whatsNewOpenTimeMs += (now - whatsNewTimestamp);
    whatsNewTimestamp = null;
  }
});

watch(showAboutData, (show: boolean) => {
  const now = Date.now();
  if (show) {
    aboutDataTimestamp = now;
    aboutDataOpenedCount += 1;
  } else if (aboutDataTimestamp !== null) {
    aboutDataOpenTimeMs += (now - aboutDataTimestamp);
    aboutDataTimestamp = null;
  }
});

watch(showCredits, (show: boolean) => {
  const now = Date.now();
  if (show) {
    creditsTimestamp = now;
    creditsOpenedCount += 1;
  } else if (creditsTimestamp !== null) {
    creditsOpenTimeMs += (now - creditsTimestamp);
    creditsTimestamp = null;
  }
});

watch(introductionOpen, (open: boolean) => {
  const now = Date.now();
  if (open) {
    introductionTimestamp = now;
    introductionOpenedCount += 1;
  } else if (introductionTimestamp !== null) {
    introductionOpenTimeMs += (now - introductionTimestamp);
    introductionTimestamp = null;
  }
});

watch(userGuideOpen, (open: boolean) => {
  const now = Date.now();
  if (open) {
    userGuideTimestamp = now;
    userGuideOpenedCount += 1;
  } else if (userGuideTimestamp !== null) {
    userGuideOpenTimeMs += (now - userGuideTimestamp);
    userGuideTimestamp = null;
  }
});

const radioSubloc: ComputedRef<[number | null, number | null]> = computed(() => [radio.value, sublocationRadio.value]); 
watch(radioSubloc, (item: [number | null, number | null]) => {
  const radio = item[0];
  const subRadio = item[1];
  if (radio === null || subRadio === null) {
    return [];
  }
  const eventText = interestingEvents[radio].label ?? "";
  const subEventText = locationsOfInterest.value[radio][subRadio].text;
  userSelectedNotableEvents.push([eventText, subEventText]);
});

watch(selectedTimezone, (timezone: string) => {
  userSelectedTimezones.push(timezone);
});

watch(singleDateSelected, (date: Date) => {
  if (!timestampsLoaded.value ) return;
  userSelectedCalendarDates.push(date.getTime());
});
</script>
  
<style lang="less">
@font-face {
  font-family: "Highway Gothic Narrow";
  src: url("./assets/HighwayGothicNarrow.ttf");
}

// import Lexand from google fonts
// @import url('https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap');

:root {
  // font-size: clamp(14px, 1.7vw, 16px);
  // --default-font-size: 1rem; // we don't use this
  font-size: 16px; // this is the standard browser default
  --default-line-height: clamp(1rem, min(2.2vh, 2.2vw), 1.6rem); // we don't use this
  --smithsonian-blue: #009ade;
  --smithsonian-yellow: #ffcc33;
  --info-background: #092088;
  --map-height: 500px;
}

.dp__theme_dark {
  --dp-primary-text-color: #fff !important; // selected date text
  --dp-primary-color: var(--accent-color) !important; // selected date background
}

.dp__month_year_select,
.dp__calendar_item {
  color: #97c8f1 !important; // selectable date text & Month/Year
  font-weight: 800 !important;
}

.dp__cell_disabled {
  color: #888 !important;
  font-weight: 400;
}

button.dp__action-latest {
  color: white;
  background: var(--dp-primary-color);
}

button.dp__action-latest[disabled] {
  background: var(--dp-disabled-color);
  color: #ccc;
  
}

html {
  height: 100%;
  margin: 0;
  padding: 0;
  background-color: #000;
  overflow: hidden;


  -ms-overflow-style: none;
  // scrollbar-width: none;
}

body {
  position: fixed;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  overflow: auto;

  font-family: Verdana, Arial, Helvetica, sans-serif;
}

a {
  text-decoration: none;
  color: var(--smithsonian-yellow);
}

ul {
  margin-left: 1rem;
}

#intro-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100% !important;
  height: 100% !important;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 100;
}

.gradient-background {
  // rotated translucent background gradient
  background: linear-gradient(45deg,
      rgb(14, 30, 40),
      rgb(22, 50, 65),
      rgb(30 70 90));
}

#introduction-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  height: fit-content;
  // outline: 5px solid var(--accent-color);
  border-radius: 1em;
  z-index: 1000;

  @media (max-width: 700px) {
    width: 95%;
    padding: 3em 1em 1em;
  }

  @media (min-width: 701px) {
    width: 75%;
    padding: 3em 2em 2em;
  }

  .span-accent {
    color: var(--accent-color);
  }


  //font-size: calc(1.1 * var(--default-font-size));
  // line-height: var(--default-line-height);

  .v-list-item {
    color: #eee;
  }

  .intro-text {
    color: white;
    font-size: 1em;
    line-height: 1.5em;
  }

  strong {
    color: white;
  }

  div#intro-bottom-controls {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;

    gap: 1em;
    margin-top: 0.5em;

    .v-btn.v-btn--density-default {
      max-height: calc(1.6 * var(--default-line-height));
    }

    .v-btn--size-default {
      font-size: calc(0.9 * var(--default-font-size));
    }

    #intro-next-button {
      background-color: rgba(18, 18, 18, .5);
    }
  }
}

#privacy-popup-dialog {

  .v-card-text {
    color: #BDBDBD;
  }

  .v-overlay__content {
    font-size: var(--default-font-size);
    background-color: purple;
    position: absolute;
    bottom: 0;
    right: 0;
  }

  .v-btn--size-default {
      font-size: calc(0.9 * var(--default-font-size));
    }  

  .v-card-actions .v-btn {
    padding: 0 4px;
  }
}

#splash-screen {
  color: #E0E0E0;

  justify-content: space-around;
  align-content: center;
  padding-top: 4rem;
  padding-bottom: 1rem;

  font-family: 'Highway Gothic Narrow', 'Roboto', sans-serif;

  div {
    margin-inline: auto;
    text-align: center;
  }

  // make a paragraph inside the div centered horizontally and vertically
  p {
    font-family: 'Highway Gothic Narrow', 'Roboto', sans-serif;
    font-weight: bold;
    vertical-align: middle;
  }

  #first-splash-row {
    width: 100%;
    font-size: 1.25em;
  }

  #splash-screen-text {
    // in the grid, the text is in the 2nd column
    display: flex;
    flex-direction: column;
    line-height: 130%;
    font-size: 2em;
    color: var(--smithsonian-yellow);
  }

  .splash-get-started {
    border: 2px solid white;
    font-size: 1.5em;
    margin-top: 10%;
    margin-bottom: 2%;
    font-weight: bold !important;
  }

  #splash-screen-acknowledgements {
    margin-top: 3rem;
    font-size: calc(1.7 * var(--default-font-size));
    line-height: calc(1.5 * var(--default-line-height));
    width: 80%;
    color: var(--smithsonian-yellow);
  }

  #splash-screen-logos {
    margin-block: 0.75em;

    /* format for including more inline logos */
    display: flex; // place on same line
    justify-content: center; // align center

    >* {
      margin-inline: 0; // counteract margin-inline: auto from #splash-screen div
    }

    /* ************ */

    img {
      height: 5vmin;
      vertical-align: middle;
      margin: 2px;
    }

    @media only screen and (max-width: 600px) {
      img {
        height: 24px;
      }
    }

    svg {
      vertical-align: middle;
      height: 24px;
    }
  }
}

#intro-window-close-button {
  position: absolute;
  top: 0.25em;
  right: 0.25em;

  &:hover {
    cursor: pointer;
  }
}

#main-content {
  position: fixed;
  width: 100%;
  height: var(--app-content-height);
  overflow: auto;
  transition: height 0.1s ease-in-out;
}

#app {
  width: 100%;
  height: 100%;
  margin: 0;
  overflow: hidden;
}

#map {
  width: 100%;
  height: var(--map-height)
}

// define the layout
.content-with-sidebars {
  position: relative;
  padding: 0;

  display: grid;
  grid-template-columns: 0 .8fr .3fr;
  grid-template-rows: 70px var(--map-height) 78px .5fr .5fr;
  gap: 20px 10px;

  >* {
    background-color: transparent;
  }

  >div {
    outline: 1px solid transparent;
  }

  #user-options {
    min-width: 200px;
    margin-left: 1.5rem;
    grid-column: 3 / 4;
    grid-row: 2 / 3;
  }

  #logo-title {
    display: flex;
    align-items: center;
    grid-column: 2 / 4;
    grid-row: 1 / 2;
    gap: 10px;
  }

  #menu-area {
    // grid-column: 3 / 4;
    // grid-row: 1 / 2;
    // // border: 1px solid red;
    display: flex;
    justify-self: end;
    gap: 1rem;
    align-items: center;
  }

  #where {
    display: none;
    grid-column: 1 / 2;
    grid-row: 2 / 3;
  }

  #map-container {
    grid-column: 2 / 3;
    grid-row: 2 / 3;
  }

  #when {
    display: none;
    grid-column: 1 / 2;
    grid-row: 3 / 4;
  }

  #slider-row {
    grid-column: 2 / 3;
    grid-row: 3 / 4;
  }

  #information {
    padding: 1rem;
    grid-column: 2 / 3;
    grid-row: 4 / 6;
  }

}

// style the content 
#main-content {
  padding: 2rem;
}

.content-with-sidebars {
  font-family: "Lexend", sans-serif;
  font-optical-sizing: auto;
  font-weight: normal;
  font-style: normal;
  background-color: transparent;
}

#title {
  color: var(--smithsonian-yellow);
  font-weight: 600;
  font-size: 2.5rem;
  text-align: left;
  text-wrap: nowrap;
  flex-grow: 1;
}

a[href="https://tempo.si.edu"]>img {
  // display: inline;
  // height: 70px !important;
  width: 100px !important;
  max-width: 10vmin;
  min-width: 50px;
}

#information {
  background-color: var(--info-background);
  border-radius: 10px;
  padding-inline: 1rem;
  // margin-right: 200px;
}

a {
  text-decoration: underline;
  font-weight: bold;
  color: var(--accent-color-2);
}


// prevent overflows of the content
#user-options {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 5px;
}

#date-radio {
  padding-block: 0.5rem;
}

#date-radio div.highlighted label {
  font-size: 1.2em;
  font-weight: bold;
  border-radius: 5px;
  color: var(--smithsonian-yellow);
  opacity: 1;
}

#all-dates {
  padding-bottom: 0.5rem;
}

#locations-of-interest {
  border: 1px solid black;
  padding-block: 0.5rem;
  padding-inline: 1rem;
  height: fit-content;

  h3 {
    font-weight: 500;
  }
}

.big-label {
  font-size: 2rem;
  text-align: right;
  margin-right: 0.5rem;
  align-self: end;
  color: var(--smithsonian-blue);
}

#when {
  align-self: start;
}

#slider-row,
#when {
  margin-top: 1.5rem;
}

#slider-row {
  margin-left: 3rem;
}

#map-container {
  position: relative;
  display: flex;
  flex-direction: row;
  padding-right: 10px;

  #map {
    flex-basis: 80%;
    flex-grow: 1;
    flex-shrink: 1;
  }

  #location-and-sharing {
    position: absolute;
    bottom: 0;
    z-index: 1000;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
    width: fit-content;
  }

  .forward-geocoding-container {
    width: 250px;
    max-width: 50vw;
    border: 2px solid black;
  }

  #map-show-hide-controls {
    z-index: 1000;
    position: absolute;
    top: 1rem;
    right: 80px;
  }

  #map-legend {
    position: absolute;
    top: 0;
    right: 80px;
    width: fit-content;
    z-index: 1000;

    display: flex;
    align-items: center;
    gap: 0.5rem;

    color: black;
    background-color: #fff5;
    padding-left: 0.5rem;
    padding-right: 0.25rem;

    backdrop-filter: blur(5px);

    hr.line-legend {
      display: inline-block;
      border: 0.5px solid #c10124;
      width: 3rem;
    }
  }

  .colorbar-container {
    flex-grow: 0;
    flex-shrink: 1;

    .unit-label {
      font-size: .95em;
    }
  }

  #la-fires {
    z-index: 1000;
    position: absolute;
    right: 80px;
    bottom: 1rem;

  }
}

#slider-row {
  display: flex;
  flex-direction: row;
  padding-left: 0;

  >#play-pause-button {
    height: fit-content;
    align-self: center;
    padding-inline: 0.5rem;
    margin-left: 0.75rem;
    width: 2.5rem;
  }

  #play-pause-button[disabled] {
    filter: grayscale(100%);
    cursor: progress;
    cursor: not-allowed;
  }

  .icon-wrapper {
    padding-inline: 0.5rem !important;
  }
}

.time-slider {

  .v-slider-thumb {

  .v-slider-thumb__surface::after {
    background-image: url("./assets/smithsonian.png");
      background-size: 30px 30px;
      height: 30px;
      width: 30px;
    }

    .v-slider-thumb__label {
      background-color: var(--accent-color-2);
      border: 0.25rem solid var(--accent-color);
      width: max-content;
      height: 2.5rem;
      font-size: 1rem;

      &::before {
        color: var(--accent-color);
      }
    }
  }

  .v-slider-track__tick {
    background-color: var(--accent-color);
    /* Change color */
    height: 15px;
    /* Change size */
    width: 4px;
    margin-top: 0 !important;
    // top: -10%;
  }


  &.hide-ticks-1 .v-slider-track__tick:nth-last-child(-n + 1),
  &.hide-ticks-2 .v-slider-track__tick:nth-last-child(-n + 2),
  &.hide-ticks-3 .v-slider-track__tick:nth-last-child(-n + 3),
  &.hide-ticks-4 .v-slider-track__tick:nth-last-child(-n + 4),
  &.hide-ticks-5 .v-slider-track__tick:nth-last-child(-n + 5), 
  &.hide-ticks-6 .v-slider-track__tick:nth-last-child(-n + 6),
  &.hide-ticks-7 .v-slider-track__tick:nth-last-child(-n + 7)
  {
    display: none !important;
  }

  .v-slider {

    .v-slider.v-input--horizontal {
      grid-template-rows: auto 0px;
    }

    .v-slider.v-input--horizontal .v-slider-thumb__label {
      // top: calc(var(--v-slider-thumb-size) * 1.5);
      z-index: 2000;
    }

    .v-slider.v-input--horizontal .v-slider-thumb__label::before {
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-bottom: 6px solid transparent;
      border-top: 6px solid currentColor;
      bottom: -15px;
    }
  }
}

.time-slider.hide-first-last-ticks.v-input--disabled {
    .v-slider__container {
      opacity: 100 !important;
    }
    
    .v-slider-track__tick--first,
    .v-slider-track__tick--last {
      display: none;
  }
}

#opacity-slider-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-left: 7%;
  padding-right: 7%;
  gap: 2px;

  .v-slider {
    margin-right: 0;
    width: 100%;
  }

  #opacity-slider-label {
    opacity: 0.7;
    width: fit-content;
  }
}

#body-logos {
  margin-bottom: -1rem;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;

  img {
    height: 35px !important;
    vertical-align: middle;
    margin: 2px;
  }
}

#icons-container>a[href="https://worldwidetelescope.org/home/"] {
  display: none;
}

.v-selection-control {
  z-index: auto;

}

.v-radio-group .v-input__details {
  display: none;
}

.v-radio-group .v-selection-control {
  label {
    width: 100%;
  }
}

.rounded-icon-wrapper {
  height: fit-content;
  align-self: center;
  padding-inline: 0.5rem;
  margin-left: 0.75rem;
  width: 2.5rem;
  color: var(--accent-color);
  border: 2px solid var(--accent-color);
  border-radius: 20px;
}

i.mdi-menu-down {
  color: var(--smithsonian-blue);
}

// From Sara Soueidan (https://www.sarasoueidan.com/blog/focus-indicators/) & Erik Kroes (https://www.erikkroes.nl/blog/the-universal-focus-state/)
:focus-visible:not(.v-field__input input, .v-overlay__content),
button:focus-visible,
.focus-visible,
.v-selection-control--focus-visible .v-selection-control__input {
  outline: 9px double white !important;
  box-shadow: 0 0 0 6px black !important;
  border-radius: .125rem;
}

.controls-card {
  padding: 1rem;
  border: 1px solid #068ede;
}

//  mobile styles

// ========= DEFINE MOBILE STYLES =========
// KEEP THEM ALL HERE
@media (max-width: 1180px) {

  .content-with-sidebars {
    grid-template-columns: 0px auto auto;
    grid-template-rows: 3.5rem var(--map-height) 78px .5fr .5fr;

    #when {
      display: none;
    }

    #where {
      display: none;
    }

    #title {
      text-wrap: wrap;
      font-size: 2rem;
      line-height: 1.25;
      margin-left: 10px;
    }

    #slider-row {
      margin-left: 3rem;
    }

    #user-options {
      width: 250px;
    }


  }
}

@media (max-width: 750px) {
  :root {
    --map-height: 45vh;
    --map-height: 45dvh;
    --map-height: 45svh;
    font-size: 14px;
  }

  #main-content {
    padding: 1rem;
  }

  #introduction-overlay .v-window {
    max-height: 75vh;
    max-height: 75dvh;
    max-height: 75sdvh;
    overflow-y: scroll;
  }

  #introduction-overlay .intro-text {
    font-size: min(1.15em, 2vw);
  }

  #introduction-overlay ul li {
    margin-block-start: 1.15em;
  }

  .content-with-sidebars {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto 78px repeat(5, auto);
    gap: 5px;
    // padding-inline: 1rem;


    #logo-title {
      min-width: 0;
      grid-column: 1 / 2;
      grid-row: 1 / 2;
    }

    a[href="https://tempo.si.edu"]:has(img) {
      grid-column: 1 / 2;
      grid-row: 1 / 2;
    }

    #menu-area {
      grid-column: 1 / 2;
      grid-row: 1 / 2;
      display: flex;
      flex-direction: row;
      gap: 1rem;
      align-items: flex-end;
    }

    #map-container {
      grid-column: 1 / 2;
      grid-row: 2 / 3;
    }

    #map-container #map-show-hide-controls {
      right: 5px
    }

    #slider-row {
      grid-column: 1 / 2;
      grid-row: 3 / 4;
    }

    #user-options {
      grid-column: 1 / 2;
      grid-row: 4 / 5;
    }


    #where {
      display: none;
    }


    #when {
      display: none;
    }


    #bottom-options {
      grid-column: 1 / 2;
      grid-row: 5 / 6;
    }

    #information {
      grid-column: 1 / 2;
      grid-row: 6 / 7;
    }

    // #body-logos {
    //   grid-column: 1 / 2;
    //   grid-row: 7 / 8;
    // }
  }

  .content-with-sidebars {

    #slider-row {
      margin-left: 4rem;
      margin-right: 1rem;
      padding-top: 10px;
      align-items: center;
    }

    #user-options {
      margin: 0;
      margin-top: -8px;
      width: auto;
    }

    #bottom-options {
      margin: 0;
    }

    #information {
      font-size: 1em;
    }


    #title {
      font-size: clamp(1.2rem, 4vw, 2rem);
      margin-left: 15px;
      text-wrap: wrap;
    }
    
    // a[href="https://tempo.si.edu"]>img {
    //   // display: inline;
    //   height: 50px !important;
    //   width: auto !important;
    // }

  }
  

  #map-container {
    display: flex;
    flex-direction: column;


    #map-contents {
      position: relative;
    }

    #map-legend {
      right: 0;
    }

    .colorbar-container-horizontal {
      margin-top: 1rem;
      margin-bottom: 0.5rem;
      z-index: 5000;
      --height: 0.75rem;
    }

  }

  #all-dates {
    
    h2 {
      font-size: 1.2em;
    }
    
  }
}

@media (max-width: 455px) {
    .content-with-sidebars {
      #title {
        font-size: 1.2em;
    }
  }
}

/* Leaflet crispness override */
// @JobLeonard - https://github.com/Leaflet/Leaflet/issues/5883#issue-269071844
// .leaflet-container .leaflet-overlay-pane svg,
// .leaflet-container .leaflet-marker-pane img,
// .leaflet-container .leaflet-shadow-pane img,
// .leaflet-container .leaflet-tile-pane img,
.leaflet-container img.leaflet-image-layer {
  max-width: none !important;
  /* Preserve crisp pixels with scaled up images */
  image-rendering: optimizeSpeed;
  /* Legal fallback */
  image-rendering: -moz-crisp-edges;
  /* Firefox        */
  image-rendering: -o-crisp-edges;
  /* Opera          */
  image-rendering: -webkit-optimize-contrast;
  /* Safari         */
  image-rendering: optimize-contrast;
  /* CSS3 Proposed  */
  image-rendering: crisp-edges;
  /* CSS4 Proposed  */
  image-rendering: pixelated;
  /* CSS4 Proposed  */
  -ms-interpolation-mode: nearest-neighbor;
  /* IE8+           */
}

.cds-snackbar-alert {
  position: absolute;
  top: 1rem;
  right: 1rem;
  pointer-events: auto;
  z-index: 999;
}

.snackbar-alert-ul {
  margin-left: 1em;
}

@media (max-width: 750px) {
  .cds-snackbar-alert {
    top: -1rem;
  }
}

.menu-button,
.share-button,
.whats-new-button {
  outline: 1px solid var(--smithsonian-yellow) !important;
  height: 2rem !important;
}

.whats-new-button {
  padding-inline: 10px;
}

div.callout-wrapper {
  display: content;
  // overflow-x: hidden;  
}

.menu-link {
  text-decoration: none;
}

#loading-circle-progress-container {
  font-size: large;
}

#la-fires {
  max-width: 20ch;

  .v-btn {
    height: fit-content; // calc(var(--v-btn-height) + 18px);
    padding-block: 5px;
  }

  .v-btn__content {
    white-space: normal;

  }
}

.la-fires-cds-dialog .cds-dialog-card .v-card-text {
  height: unset;
}

.pulse {
  animation-name: pulse;
  animation-duration: 1.5s;
  animation-iteration-count: 3;
}


@keyframes pulse {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.2);
  }

  100% {
    transform: scale(1);
  }
}

canvas.maplibregl-canvas {
  background-color: whitesmoke;
}

#rating-root.rating-root {
  position: absolute !important;
  right: 5px;
  bottom: 0;
  padding: 5px;
  width: fit-content !important;
  // left: 50%;
  // transform: translateX(-50%);
  gap: 0 !important;
  border: solid 1px #EFEFEF !important;
  border-radius: 10px !important;
  background-color: #222222 !important;
  opacity: 0.95 !important;
  z-index: 20000;

  .rating-title {
    color: #EFEFEF;
    font-size: var(--default-font-size);
  }

  .rating-icon-row {
    
    padding: 0px;

    .svg-inline--fa {
      height: 30px;
    }
  }

  .comments-box {
    width: 100%;
    margin-top: 20px;
  }

  .v-card-text {
    padding-bottom: 0;
  }

  .v-card-actions {
    padding: 0;
  }

  #user-experience-footer {
    margin: auto;
    display: flex;
    flex-direction: row;
    gap: 5px;
  }

}

</style>
  
